/**
 * Three-way merge of level documents for lobby editing: what changed from `base` in `mine` and in
 * `theirs` is combined, so two people editing different things never undo each other.
 *
 * Units: each object, terrain shape and other top-level field is one unit; tile layers, platforms
 * (bodies) and the level merge field by field; tile, grass, spike and foliage paint merge cell by cell.
 * A unit both sides changed differently is a conflict, resolved toward `prefer`.
 */
import type { LevelDoc } from '../model/format.ts';

export interface MergeResult {
  doc: LevelDoc;
  /** paths of units both sides changed differently */
  conflicts: string[];
  /** ids of entities (objects, shapes, layers, platforms) `theirs` changed, added or removed */
  theirChanges: Set<string>;
}

type Prefer = 'mine' | 'theirs';
type Json = unknown;
interface Ctx {
  prefer: Prefer;
  conflicts: string[];
  theirChanges: Set<string>;
}

/** Flat [x, y, ...] cell arrays and their stride. */
const CELL_MAPS: Record<string, number> = { grassPaint: 3, grassLines: 3, spikeLines: 3, decoPaint: 3, foliagePaint: 3 };
const LISTS = new Set(['layers', 'terrains', 'objects', 'bodies']);
/** bookkeeping either side may touch without it being a conflict */
const QUIET = new Set(['updatedAt']);

export function mergeLevels(base: LevelDoc, mine: LevelDoc, theirs: LevelDoc, prefer: Prefer): MergeResult {
  const ctx: Ctx = { prefer, conflicts: [], theirChanges: new Set() };
  const doc = mergeContainer('', base as unknown as Rec, mine as unknown as Rec, theirs as unknown as Rec, ctx) as unknown as LevelDoc;
  return { doc, conflicts: ctx.conflicts, theirChanges: ctx.theirChanges };
}

export function same(a: Json, b: Json): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!same(a[i], b[i])) return false;
    return true;
  }
  if (Array.isArray(b)) return false;
  const ra = a as Record<string, Json>, rb = b as Record<string, Json>;
  for (const k in ra) if (!same(ra[k], rb[k])) return false;
  for (const k in rb) if (!(k in ra) && rb[k] !== undefined) return false;
  return true;
}

/** One unit: take the side that changed it. */
function pick(path: string, base: Json, mine: Json, theirs: Json, ctx: Ctx): Json {
  if (same(mine, theirs) || same(base, theirs)) return mine;
  if (same(base, mine)) return theirs;
  ctx.conflicts.push(path);
  return ctx.prefer === 'mine' ? mine : theirs;
}

type Rec = Record<string, Json>;
const keysOf = (...xs: (Rec | undefined)[]) => [...new Set(xs.flatMap((x) => (x ? Object.keys(x) : [])))];

/** The level or a platform: lists by id, paint by cell, everything else as one unit per field. */
function mergeContainer(path: string, base: Rec | undefined, mine: Rec, theirs: Rec, ctx: Ctx): Rec {
  if (base && same(base, mine)) return theirs;
  if (same(base, theirs) || same(mine, theirs)) return mine;
  const out: Rec = {};
  for (const k of keysOf(base, mine, theirs)) {
    const b = base?.[k], m = mine[k], t = theirs[k];
    let v: Json;
    if (LISTS.has(k)) v = mergeList(`${path}${k}`, k, (b as Rec[]) ?? [], (m as Rec[]) ?? [], (t as Rec[]) ?? [], ctx);
    else if (k in CELL_MAPS) v = m === undefined && t === undefined ? undefined : mergeCells(`${path}${k}`, CELL_MAPS[k], b as number[], m as number[], t as number[], ctx);
    else if (k === 'palette') v = mergePalette(`${path}${k}`, b as Json[], m as Json[], t as Json[], ctx);
    else if (QUIET.has(k)) v = ctx.prefer === 'mine' ? m ?? t : t ?? m;
    else v = pick(`${path}${k}`, b, m, t, ctx);
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function mergeLayer(path: string, base: Rec | undefined, mine: Rec, theirs: Rec, ctx: Ctx): Rec {
  if (base && same(base, mine)) return theirs;
  if (same(base, theirs) || same(mine, theirs)) return mine;
  const out: Rec = {};
  for (const k of keysOf(base, mine, theirs)) {
    const v = k === 'tiles' ? mergeCells(`${path}.tiles`, 4, base?.[k] as number[], mine[k] as number[], theirs[k] as number[], ctx) : pick(`${path}.${k}`, base?.[k], mine[k], theirs[k], ctx);
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function mergeItem(path: string, list: string, base: Rec | undefined, mine: Rec, theirs: Rec, ctx: Ctx): Rec {
  if (list === 'bodies') return mergeContainer(`${path}.`, base, mine, theirs, ctx);
  if (list === 'layers') return mergeLayer(path, base, mine, theirs, ctx);
  return pick(path, base, mine, theirs, ctx) as Rec;
}

const idOf = (x: Rec) => String(x.id);

/** Every id inside a platform too, so a lock on a shape inside it still guards it. */
function noteIds(x: Rec | undefined, into: Set<string>) {
  if (!x) return;
  into.add(idOf(x));
  for (const k of LISTS) for (const c of (x[k] as Rec[] | undefined) ?? []) noteIds(c, into);
}

function mergeList(path: string, list: string, base: Rec[], mine: Rec[], theirs: Rec[], ctx: Ctx): Rec[] {
  if (same(base, theirs) || same(mine, theirs)) return mine;
  const B = new Map(base.map((x) => [idOf(x), x])), M = new Map(mine.map((x) => [idOf(x), x])), T = new Map(theirs.map((x) => [idOf(x), x]));
  const merged = new Map<string, Rec>();
  for (const id of new Set([...B.keys(), ...M.keys(), ...T.keys()])) {
    const b = B.get(id), m = M.get(id), t = T.get(id);
    if (!same(b, t)) {
      if (list === 'bodies') {
        // a platform counts as changed by them only where its own content differs
        const before = new Set<string>(), after = new Set<string>();
        noteIds(b, before);
        noteIds(t, after);
        if (!b || !t) for (const x of [...before, ...after]) ctx.theirChanges.add(x);
        else {
          ctx.theirChanges.add(id);
          for (const k of LISTS) {
            const bl = new Map(((b[k] as Rec[]) ?? []).map((x) => [idOf(x), x]));
            for (const c of (t[k] as Rec[]) ?? []) if (!same(bl.get(idOf(c)), c)) noteIds(c, ctx.theirChanges);
            const tl = new Set(((t[k] as Rec[]) ?? []).map(idOf));
            for (const [cid] of bl) if (!tl.has(cid)) ctx.theirChanges.add(cid);
          }
        }
      } else ctx.theirChanges.add(id);
    }
    const p = `${path}[${id}]`;
    if (m && t) merged.set(id, mergeItem(p, list, b, m, t, ctx));
    else if (m) {
      // they removed it (or never had it): gone unless I changed it since base
      if (!b) merged.set(id, m);
      else if (!same(b, m)) {
        ctx.conflicts.push(p);
        if (ctx.prefer === 'mine') merged.set(id, m);
      }
    } else if (t) {
      if (!b) merged.set(id, t);
      else if (!same(b, t)) {
        ctx.conflicts.push(p);
        if (ctx.prefer === 'theirs') merged.set(id, t);
      }
    }
  }
  return order(base.map(idOf), mine.map(idOf), theirs.map(idOf), merged);
}

/** Keep the order of whichever side reordered, then slot in the other side's new items after their neighbours. */
function order(base: string[], mine: string[], theirs: string[], items: Map<string, Rec>): Rec[] {
  const inBase = new Set(base);
  const common = (xs: string[], other: Set<string>) => xs.filter((id) => inBase.has(id) && other.has(id));
  const mineSet = new Set(mine);
  const mineMoved = !same(common(mine, inBase), common(base, mineSet));
  const [first, second] = mineMoved ? [mine, theirs] : [theirs, mine];
  const out = first.filter((id) => items.has(id));
  const placed = new Set(out);
  let prev: string | null = null;
  for (const id of second) {
    if (items.has(id) && !placed.has(id)) {
      const at = prev === null ? 0 : out.indexOf(prev) + 1;
      out.splice(at, 0, id);
      placed.add(id);
    }
    if (placed.has(id)) prev = id;
  }
  for (const id of items.keys()) if (!placed.has(id)) out.push(id);
  return out.map((id) => items.get(id)!);
}

function mergeCells(path: string, stride: number, base: number[] | undefined, mine: number[] | undefined, theirs: number[] | undefined, ctx: Ctx): number[] | undefined {
  if (same(mine, theirs) || same(base, theirs)) return mine;
  if (same(base, mine)) return theirs;
  const read = (a: number[] | undefined) => {
    const m = new Map<string, number[]>();
    if (a) for (let i = 0; i + stride <= a.length; i += stride) m.set(`${a[i]},${a[i + 1]}`, a.slice(i, i + stride));
    return m;
  };
  const B = read(base), M = read(mine), T = read(theirs);
  const out: number[] = [];
  for (const key of new Set([...T.keys(), ...M.keys(), ...B.keys()])) {
    const v = pick(`${path}(${key})`, B.get(key), M.get(key), T.get(key), ctx) as number[] | undefined;
    if (v) out.push(...v);
  }
  return out;
}

/** Palettes only grow while painting: take the longer when one extends the other. */
function mergePalette(path: string, base: Json[] | undefined, mine: Json[] | undefined, theirs: Json[] | undefined, ctx: Ctx): Json {
  const m = mine ?? [], t = theirs ?? [];
  const prefix = (a: Json[], b: Json[]) => a.length <= b.length && a.every((x, i) => same(x, b[i]));
  if (prefix(m, t)) return theirs;
  if (prefix(t, m)) return mine;
  return pick(path, base, mine, theirs, ctx);
}
